import * as React from 'react'
import styled from 'styled-components'

const ButtonGroup = styled.div`
  display: flex;
  ${ props => props.theme.roundCornerInsideTooltips && 'border-radius: 0.25em;' }
`

const ButtonItem = styled.span<{ $itemByLine: number, $selected: boolean, $disabled: boolean }>`
  font-weight: bold;
  cursor: pointer;
  width: ${ props => 100 / props.$itemByLine }%;
  font-size: 0.875em;
  text-align: center;
  padding: 0.71429em 0.35714em 0.71429em 0.35714em;
  background: ${ props => props.theme.colors.clr_0_lightest };
  margin-bottom: 0.625em;
  
  &:not(:last-child) {
    border-right: 1px solid ${ props => props.theme.colors.clr_0_light };
  }

  ${ props => props.$disabled && 'opacity: 0.3;' }

  ${ props => props.$selected && `
    background-color: ${ props.theme.colors.clr_1 };
    color: white;

    ${ props.theme.roundCornerInsideTooltips && `
      &:first-child {
        border-top-left-radius: 0.25em;
        border-bottom-left-radius: 0.25em;
      }

      &:last-child {
        border-top-right-radius: 0.25em;
        border-bottom-right-radius: 0.25em;
      }
    `}
  ` }
`

interface Props<T> {
  data: { value: T, text: string, disabled?: boolean }[]
  name: string
  onChange: (selected: T) => void
  itemByLine?: number
  value: T
  extraClasses?: string
  autoSelect?: boolean
  style?: React.CSSProperties
  reemitOnClick?: boolean
}

export default class ButtonGroupComponent<T> extends React.Component<Props<T>, never> {
  static defaultProps = { autoSelect: true }
  
  componentDidMount () {
    // if user has only one choice, pre-select it
    if (this.props.autoSelect) {
      const enabledData = this.props.data.filter(value => !value.disabled)
      if (enabledData.length === 1 && enabledData[0].value !== this.props.value) this.props.onChange(enabledData[0].value)
    }
  }

  shouldComponentUpdate (nextProps: Props<T>) {
    return this.props.data !== nextProps.data || this.props.value !== nextProps.value
  }

  render () {
    const { data, value, name, style, reemitOnClick: reemitonClick, onChange } = this.props
    const itemByLine = this.props.itemByLine || 2

    const dataRows: Props<T>['data'][] = []
    for (let i = 0; i < data.length; i += itemByLine) dataRows.push(data.slice(i, i + itemByLine))

    return <div style={ style || { marginBottom: '0.625em' } }>
      { dataRows.map((row, rowIndex) => <ButtonGroup key={ rowIndex }>
        { row.map(item =>
          <ButtonItem
            id={ `${name}${data.indexOf(item)}` }
            key={ item.value?.toString() }
            className={ item.value === value ? 'button-group-item-selected' : '' }
            $itemByLine={ itemByLine }
            $selected={ item.value === value }
            $disabled={ item.disabled }
            onClick={ () => !item.disabled && (reemitonClick || item.value !== value) && onChange(item.value) }
            children={ item.text } />
        ) }
      </ButtonGroup> ) }
    </div>
  }
}
